import { logger } from '@gik/analytics/utils/logger';
import { addBillingAddress, updateBillingAddress } from '@gik/api/users/billingAddresses';
import { useUser } from '@gik/api/users/user';
import { optimisticUpdate } from '@gik/core/api';
import type { GikBillingAddressModel } from '@gik/core/models/gik/BillingAddress';
import type { BaseUserWithLocation } from '@gik/core/models/gik/User';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { UI } from '@gik/ui-legacy/UIManager';
import type { BillingAddressFormProps } from '@gik/ui-legacy/gik/BillingAddressForm/BillingAddressForm';
import { BillingAddressForm } from '@gik/ui-legacy/gik/BillingAddressForm/BillingAddressForm';
import React from 'react';

export type UserBillingAddressFormProps = {
  user: BaseUserWithLocation;
  billingAddress: GikBillingAddressModel;
  onSuccess: () => void;
} & BillingAddressFormProps;

function UserBillingAddressFormComp({
  user,
  billingAddress,
  onSuccess,
  ...otherProps
}: React.PropsWithChildren<UserBillingAddressFormProps>): React.ReactElement {
  const bem = useBemCN('user-billing-address-form');
  const { data: fullUser, mutate } = useUser(user.id);

  const handleSubmit = React.useCallback(
    async values => {
      const rollback = { ...fullUser };
      const updatedUser = {
        ...fullUser,
        billingAddresses: [values],
      };
      // add billing address if the user doesn't have one yet
      if (!billingAddress) {
        await optimisticUpdate(updatedUser, null, mutate, async () => {
          const response = await addBillingAddress(user.id, values);

          if (!response.ok) {
            UI.notifySuccess('Failed to add Billing Address');
            logger.error(`Billing Address: failed to add address for userId "${user.id}"`);
            return void mutate(rollback);
          }

          UI.notifySuccess('Billing Address Added');
          onSuccess?.();
        });

        return;
      }

      await optimisticUpdate(updatedUser, null, mutate, async () => {
        // update existing billing address
        const response = await updateBillingAddress(user.id, billingAddress.id, { ...values, primary: false });

        if (!response.ok) {
          UI.notifySuccess('Failed to add Billing Address');
          logger.error(`Billing Address: failed to update address "${billingAddress.id}" for userId "${user.id}"`);
          return void mutate(rollback);
        }

        UI.notifySuccess('Billing Address Updated');
        onSuccess?.();
      });
    },
    [billingAddress, fullUser, mutate, onSuccess, user.id]
  );

  return (
    <BillingAddressForm
      onSubmit={handleSubmit}
      user={user}
      billingAddress={billingAddress}
      {...otherProps}
      {...bem()}
    />
  );
}

export const UserBillingAddressForm = withComponentErrorBoundary(UserBillingAddressFormComp);
